import React, { useState, useEffect, createContext, Context } from 'react';
import { useDispatch } from 'react-redux';
import Provider from 'src/domains/query/Provider';
import { Typography, Dialog, Switch, FormControlLabel, Button } from '@mui/material'
import * as types from 'src/domains/query/types';
import * as actions from '../actions';
import { hasText } from 'src/utils';
import { reset as resetFilter } from '../filter/actions';
import { setDisabled } from '../filter/actions';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Radio from '@mui/material/Radio';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { navigate, changePageTitle } from 'src/domains/global/actions';
import { deleteAction, createNew } from 'src/domains/explorer/actions';
import { ID } from 'src/domains/explorer/types';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import MoreVert from '@mui/icons-material/MoreVert';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import Edit from '@mui/icons-material/Edit';
import Lock from '@mui/icons-material/Lock';
import DeleteForever from '@mui/icons-material/DeleteForever';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import EntityExplorer from 'src/domains/explorer/views/EntityExplorer';
import AttrSelectorProviderDialog from './AttrSelectorProviderDialog';
import { useTheme } from '@mui/material/styles';
import { capitalize } from 'src/utils';
import _, { Dictionary } from 'lodash';
import FilterContainer from '../filter/components/FilterContainer';
import GridMenu from './GridMenu';
import { isFieldAllowed } from 'src/auth';
import { Field, getFieldLabel, getResourceLabel, conf as globalConf, Resource } from 'src/config';

// This is a static config to enable editable textarea for filter
const ENABLE_FILTER_TEXTAREA: boolean = false;

export type GridScope = {
    conf: types.ListConf,
    local: boolean,
}

export const GridContext: Context<GridScope | undefined> = createContext<GridScope | undefined>(undefined);

const DataGrid: React.FC<{
    conf: types.ListConf,
    local: boolean,
    onSelect?: (ids: ID[]) => void,
    multiple?: boolean,
    regionKey?: string,
    lockedAreNonSelectable?: boolean,
}> = ({
    conf,
    local,
    onSelect,
    multiple = false,
    regionKey = '/',
    lockedAreNonSelectable = false
}) => {
        const dispatch = useDispatch();
        const [tempId, setTempId] = useState<ID>();
        const [selectedIds, setSelectedIds] = useState<ID[]>([]);
        const [filterEditorOpen, setFilterEditorOpen] = useState<boolean>(false);
        const [attrSelectorAnchorEl, setAttrSelectorAnchorEl] = React.useState<HTMLButtonElement | null>(null);
        const [gridMenuAnchorEl, setGridMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null);
        const selectable = onSelect !== undefined;
        const { resourceName } = conf;
        const resource: Resource = globalConf.getResource(conf.resourceName);

        useEffect(() => {
            dispatch(changePageTitle(capitalize(getResourceLabel(resource))));
        }, []);

        const handleConf = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAttrSelectorAnchorEl(event.currentTarget);
        };

        const handleGridMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
            setGridMenuAnchorEl(event.currentTarget);
        };

        const handleEdit = (id: string) => {
            const pathTpl = _.template('../{resource}/{id}');
            const location = pathTpl({
                id,
                resource: resourceName
            });
            dispatch(navigate(location));
        };

        const addAction = <IconButton onClick={() => {
            dispatch(createNew(resourceName));
            setTempId('new:' + resourceName);
        }}>
            <AddIcon fontSize='small' />
        </IconButton>;

        const handleChangeQuery = (value: string) => {
            dispatch(setDisabled(true));
            dispatch(actions.setQuery(value));
        };

        const handleApplyFilter = (filter: string) => {
            dispatch(actions.setFilter(filter));
            dispatch(actions.startExistingQuery(local));
        };

        const handleClearFilter = () => {
            dispatch(resetFilter());
        };

        const gridScope = {
            conf,
            local,
        }

        return <GridContext.Provider value={gridScope}>
            <Provider conf={conf} regionKey={regionKey} local={local}>
                {({
                    local,
                    loading,
                    error,
                    response,
                    request,
                    page,
                    sort,
                    handleToggleAttr,
                    handleSort,
                    handleChangePage,
                    handleChangeRowsPerPage,
                    refresh,
                    isAdmin,
                    hidden,
                    query,
                }) => {

                    const displayFields: Dictionary<Field> = _.fromPairs(
                        _.entries(request?.attributes)
                            .filter(entry => isFieldAllowed(entry[0]) && (!entry[1].hidden || hidden))
                    );

                    return <Box sx={{ position: 'relative', mb: 1 }}>
                        {error && <Typography className={'err'} sx={{ color: (theme) => theme.palette.error.main }}>{'Error'}</Typography>}

                        {
                            isAdmin && <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={hidden === true}
                                            onChange={(e) => dispatch(actions.setShowHiddenColsAction(!hidden))}
                                        />
                                    }
                                    label={'Show hidden fields'}
                                />
                            </Box>
                        }

                        {
                            filterEditorOpen && <Box>
                                {
                                    ENABLE_FILTER_TEXTAREA && <TextareaAutosize
                                        placeholder="Search Query"
                                        minRows={3}
                                        maxRows={12}
                                        style={{ width: "100%" }}
                                        value={query}
                                        onChange={(e) => handleChangeQuery(e.target.value)}
                                    />
                                }
                                <FilterContainer hidden={hidden} />
                                <Button
                                    onClick={() => {
                                        handleApplyFilter(query);
                                    }}
                                    variant='contained'
                                    sx={{ m: 1 }}
                                    size='small'
                                >
                                    {'Apply'}
                                </Button>
                                <Button
                                    onClick={() => {
                                        handleClearFilter();
                                    }}
                                    variant='outlined'
                                    sx={{ m: 1 }}
                                    size='small'
                                >
                                    {'Clear'}
                                </Button>
                            </Box>
                        }

                        {(request && response !== undefined) && <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: theme => theme.shadows[1] }}>

                            <TableContainer sx={{ maxHeight: 'calc(100vh - 176px)' }}>
                                <Table stickyHeader aria-label="sticky table" size='small'>

                                    <TableHead>
                                        <TableRow>
                                            {selectable && <TableCell size='small' sx={{ width: 0 }}></TableCell>}
                                            <TableCell size='small' colSpan={2} padding={'none'}>
                                                <Stack
                                                    direction={'row'}
                                                    divider={<Divider orientation="vertical" flexItem />}
                                                    justifyContent={'space-around'}
                                                >
                                                    <IconButton
                                                        onClick={handleGridMenu}
                                                        sx={{ p: 0, m: 0 }}
                                                    >
                                                        <MoreVert fontSize='medium' />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => setFilterEditorOpen(!filterEditorOpen)}
                                                        sx={{ p: 0, m: 0 }}
                                                    >
                                                        <FilterAltIcon
                                                            fontSize='medium'
                                                            sx={{ color: theme => hasText(query) ? theme.palette.primary.main : undefined }}
                                                        />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={handleConf}
                                                        sx={{ p: 0, m: 0 }}
                                                    >
                                                        <ViewColumnIcon fontSize='medium' />
                                                    </IconButton>
                                                </Stack>

                                                <GridMenu anchorEl={gridMenuAnchorEl} onClose={() => setGridMenuAnchorEl(null)} />

                                                <AttrSelectorProviderDialog
                                                    anchorEl={attrSelectorAnchorEl}
                                                    listConf={gridScope.conf}
                                                    onClose={() => setAttrSelectorAnchorEl(null)}
                                                    hidden={hidden}
                                                >
                                                    {({
                                                        path,
                                                        id,
                                                    }) => {
                                                        return <ListItem
                                                            key={path.path}
                                                            secondaryAction={
                                                                <Checkbox
                                                                    edge="end"
                                                                    onChange={() => handleToggleAttr(path.path, path.field)}
                                                                    checked={_.keys(request.attributes).indexOf(path.path) > -1}
                                                                    inputProps={{ 'aria-labelledby': id }}
                                                                    size='small'
                                                                />
                                                            }
                                                            disablePadding
                                                        >
                                                            <ListItemButton>
                                                                <ListItemText id={id} primary={`${getFieldLabel(path.field)}`} />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    }}
                                                </AttrSelectorProviderDialog>
                                            </TableCell>

                                            {_.toPairs(displayFields)?.map(
                                                (attr, index) => <TableCell key={index} sx={{ whiteSpace: 'nowrap' }}>
                                                    <Box display='flex'>
                                                        <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }} >{`${getFieldLabel(attr[1])}`}</Typography>
                                                        <Box>
                                                            <IconButton
                                                                size='small'
                                                                sx={{ p: 0, m: 0 }}
                                                                onClick={() => handleSort(attr[0])}
                                                            >
                                                                {
                                                                    (sort[attr[0]] === undefined) ?
                                                                        <ArrowUpward fontSize='small' color='disabled' />
                                                                        :
                                                                        (sort[attr[0]] === types.SortOrder.Desc) ?
                                                                            <ArrowUpward fontSize='small' />
                                                                            :
                                                                            <ArrowDownward fontSize='small' />
                                                                }
                                                            </IconButton>
                                                            <IconButton size='small' sx={{ p: 0, m: 0 }}>
                                                                <MoreVert fontSize='small' />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>

                                    {!error &&
                                        <TableBody>
                                            {
                                                response.tuples.map((tuple, index) => <TableRow key={index} sx={{ '&:hover': { backgroundColor: theme => theme.palette.grey[100] } }}>
                                                    {
                                                        selectable && <TableCell size='small' sx={{ width: 0 }}>
                                                            {
                                                                lockedAreNonSelectable && tuple['_locked'] == "true" ? <>
                                                                    <Lock fontSize='small' sx={{ p: 0, m: 0, color: theme => theme.palette.text.secondary }} />
                                                                </> : (multiple ?
                                                                    <Checkbox
                                                                        checked={selectedIds.includes(tuple['_id'])}
                                                                        value={tuple['_id']}
                                                                        onChange={(e) => {
                                                                            const newSelected = e.target.checked ? [...selectedIds, tuple['_id']] : _.without(selectedIds, tuple['_id']);
                                                                            setSelectedIds(newSelected);
                                                                            onSelect!(newSelected);
                                                                        }}
                                                                    />
                                                                    :
                                                                    <Radio
                                                                        checked={selectedIds.includes(tuple['_id'])}
                                                                        value={tuple['_id']}
                                                                        onChange={() => {
                                                                            setSelectedIds([tuple['_id']]);
                                                                            onSelect!([tuple['_id']]);
                                                                        }}
                                                                    />
                                                                )
                                                            }
                                                        </TableCell>
                                                    }
                                                    <TableCell size='small' sx={{ width: 0 }}>
                                                        <IconButton
                                                            onClick={() => handleEdit(tuple['_id'])}
                                                            size='small'
                                                            sx={{ p: 0, m: 0, color: theme => theme.palette.text.secondary }}
                                                        >
                                                            <Edit fontSize='small' />
                                                        </IconButton>
                                                    </TableCell>

                                                    <TableCell
                                                        size='small'
                                                        sx={{ width: 0 }}>
                                                        <IconButton
                                                            onClick={() =>
                                                                dispatch(deleteAction(
                                                                    {
                                                                        name: resourceName,
                                                                        id: tuple['_id'],
                                                                    },
                                                                    status => {
                                                                        if (status === 'success') {
                                                                            refresh();
                                                                        }
                                                                    }
                                                                ))
                                                            }
                                                            size='small'
                                                            sx={{ p: 0, m: 0, color: theme => theme.palette.text.secondary }}
                                                        >
                                                            <DeleteForever fontSize='small' />
                                                        </IconButton>
                                                    </TableCell>

                                                    {
                                                        _.keys(displayFields)?.map((path, index) => <TableCell key={index}>
                                                            <Typography variant='inherit'>{tuple[path]}</Typography>
                                                        </TableCell>)
                                                    }
                                                </TableRow>)
                                            }
                                        </TableBody>
                                    }

                                    {!error &&
                                        <TableFooter>
                                            <TableRow>
                                                <TableCell>{addAction}</TableCell>
                                                <TableCell>
                                                    <IconButton onClick={() => {
                                                        refresh();
                                                    }}>
                                                        <RefreshIcon fontSize='small' />
                                                    </IconButton>
                                                </TableCell>
                                                <TablePagination
                                                    sx={{
                                                        position: 'sticky',
                                                        bottom: 0,
                                                        backgroundColor: theme => theme.palette.background.default,
                                                        borderTop: theme => `1px solid ${theme.palette.divider}`
                                                    }}
                                                    rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                                    colSpan={_.keys(response.attributes).length + 2}
                                                    count={response.totalCount}
                                                    rowsPerPage={response.limit || -1}
                                                    page={page}
                                                    SelectProps={{
                                                        inputProps: {
                                                            'aria-label': 'rows per page',
                                                        },
                                                        native: true,
                                                    }}
                                                    onPageChange={(event: React.MouseEvent | null, newPage: number) => {
                                                        handleChangePage(newPage);
                                                    }}
                                                    onRowsPerPageChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                                                        handleChangeRowsPerPage(parseInt(event.target.value, 10));
                                                    }}
                                                    ActionsComponent={TablePaginationActions}
                                                />
                                            </TableRow>
                                        </TableFooter>
                                    }

                                </Table>
                            </TableContainer>

                        </Paper>}

                        {
                            error && <Box
                                textAlign={'center'}
                                border={'1px solid'}
                                borderColor={theme => theme.palette.error.main}
                                pt={theme => theme.spacing(1)}
                            >
                                <WarningAmberIcon fontSize='large' color='error' />
                            </Box>
                        }

                        {
                            tempId &&
                            <Dialog
                                open={tempId !== undefined}
                                maxWidth='xl'
                                fullWidth
                                onKeyDown={(e) => (e.key === 'Escape') ? setTempId(undefined) : null}
                            >
                                <Box
                                    sx={{ p: 1 }}
                                >
                                    <EntityExplorer
                                        name={resourceName}
                                        id={tempId}
                                        onComplete={(status) => {
                                            setTempId(undefined);
                                            if (status === 'success') {
                                                refresh();
                                            }
                                        }}
                                    />
                                </Box>
                            </Dialog>
                        }
                    </Box>
                }}
            </Provider>
        </GridContext.Provider>
    }


const TablePaginationActions: React.FC<{
    backIconButtonProps?: Partial<IconButtonProps>;
    /**
     * Override or extend the styles applied to the component.
     */
    classes?: {};
    count: number;
    /**
     * Accepts a function which returns a string value that provides a user-friendly name for the current page.
     * This is important for screen reader users.
     *
     * For localization purposes, you can use the provided [translations](/guides/localization/).
     * @param {string} type The link or button type to format ('first' | 'last' | 'next' | 'previous').
     * @returns {string}
     */
    getItemAriaLabel: (type: 'first' | 'last' | 'next' | 'previous') => string;
    nextIconButtonProps?: Partial<IconButtonProps>;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
    page: number;
    rowsPerPage: number;
    showFirstButton: boolean;
    showLastButton: boolean;
}> = ({
    backIconButtonProps,
    classes,
    count,
    getItemAriaLabel,
    nextIconButtonProps,
    onPageChange,
    page,
    rowsPerPage,
    showFirstButton,
    showLastButton,
}) => {
        const theme = useTheme();

        const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
            onPageChange(event, 0);
        };

        const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
            onPageChange(event, page - 1);
        };

        const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
            onPageChange(event, page + 1);
        };

        const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
            onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
        };

        return (
            <Box sx={{ flexShrink: 0, ml: 2.5 }}>
                <IconButton
                    onClick={handleFirstPageButtonClick}
                    disabled={page === 0}
                    aria-label="first page"
                >
                    {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
                </IconButton>
                <IconButton
                    onClick={handleBackButtonClick}
                    disabled={page === 0}
                    aria-label="previous page"
                >
                    {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                </IconButton>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    aria-label="next page"
                >
                    {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                </IconButton>
                <IconButton
                    onClick={handleLastPageButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    aria-label="last page"
                >
                    {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
                </IconButton>
            </Box>
        );
    }

export default DataGrid;
